import { useState } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type { ExitClicksRow } from './exitClicksModel';
import { formatRate } from './format';

/**
 * The per-band leakage leaderboard. Ranked stores (at or above the click floor)
 * sort by leak rate DESCENDING by default, so the worst-wasting destinations sit
 * on top; columns are sortable, so leak volume descending (the magnitude view) is
 * one click away. Sub-floor stores are listed separately below, UNRANKED, because
 * their rate is too noisy to trust. Rows ABOVE the band median leak rate are
 * flagged (high is bad here, the inverse of Store performance) so the worst
 * destinations jump.
 *
 * Leak volume is the absolute matured-non-converting count, carried beside the
 * rate: it distinguishes a store wasting 90% of forty exits from one wasting 90%
 * of four thousand. It is a PROMINENT column, never the sort.
 */

type SortField = 'name' | 'totalExits' | 'leakVolume' | 'leakRate';

const COLUMNS: ReadonlyArray<{
  field: SortField;
  label: string;
  numeric: boolean;
}> = [
  { field: 'name', label: 'Store', numeric: false },
  { field: 'totalExits', label: 'Total exits', numeric: true },
  { field: 'leakVolume', label: 'Leak volume', numeric: true },
  { field: 'leakRate', label: 'Leak rate', numeric: true },
];

const compare = (
  a: ExitClicksRow,
  b: ExitClicksRow,
  field: SortField,
): number => {
  if (field === 'name') return a.name.localeCompare(b.name);
  const av = a[field];
  const bv = b[field];
  // A null rate (no matured clicks) always sorts last.
  if (av == null) return 1;
  if (bv == null) return -1;
  return (av as number) - (bv as number);
};

const StoreRow = ({
  row,
  aboveMedian,
}: {
  row: ExitClicksRow;
  aboveMedian: boolean;
}) => (
  <TableRow>
    <TableCell>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        <span>{row.name}</span>
        {aboveMedian && (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label="above median"
          />
        )}
      </Box>
    </TableCell>
    <TableCell align="right">{row.totalExits}</TableCell>
    <TableCell align="right">{row.leakVolume}</TableCell>
    <TableCell align="right">{formatRate(row.leakRate)}</TableCell>
  </TableRow>
);

export const ExitClicksTable = ({
  ranked,
  subFloor,
  medianRate,
}: {
  ranked: ExitClicksRow[];
  subFloor: ExitClicksRow[];
  medianRate: number | null;
}) => {
  // Default: leak rate descending (worst-wasting on top).
  const [sortField, setSortField] = useState<SortField>('leakRate');
  const [asc, setAsc] = useState(false);

  const onSort = (field: SortField) => {
    if (field === sortField) {
      setAsc((prev) => !prev);
    } else {
      setSortField(field);
      // Rate and volume read worst-first (descending); the name reads A-Z.
      setAsc(field === 'name');
    }
  };

  const sortedRanked = [...ranked].sort((a, b) => {
    const c = compare(a, b, sortField);
    return asc ? c : -c;
  });

  const isAboveMedian = (row: ExitClicksRow): boolean =>
    medianRate != null && row.leakRate != null && row.leakRate > medianRate;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Band median leak rate: <strong>{formatRate(medianRate)}</strong>. Rows
        above it are flagged.
      </Typography>
      <Table size="small" aria-label="stores by leakage">
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell key={col.field} align={col.numeric ? 'right' : 'left'}>
                <TableSortLabel
                  active={sortField === col.field}
                  direction={sortField === col.field && asc ? 'asc' : 'desc'}
                  onClick={() => onSort(col.field)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRanked.map((row) => (
            <StoreRow
              key={row.key}
              row={row}
              aboveMedian={isAboveMedian(row)}
            />
          ))}

          {subFloor.length > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={{ border: 0, pt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Below the click floor (rate too noisy to rank) - unranked
                  </Typography>
                </TableCell>
              </TableRow>
              {subFloor.map((row) => (
                <StoreRow key={row.key} row={row} aboveMedian={false} />
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
